import { AnimatePresence, motion } from 'framer-motion'

import { useGetContact } from '@apps-common/apis/portal/contact/get-contact'
import { AccountDetailsUpdateModal } from '@apps-common/apps/portal/pages/settings/components'
import { CardContainer } from '@apps-common/components/common/CardContainer'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { ProfileImage } from '@apps-common/components/common/user'
import { ButtonRoundSm } from '@apps-common/components/elements/button'
import { VertSpacerMd } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { useCombinedUserDetails } from '@apps-common/hooks/useCombinedUserDetails'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'

const AccountDetailsSummaryButtonSkeleton = () => (
  <CardContainer bgClasses='!bg-skeleton border border-skeleton-border !px-3 !py-2 sm:!px-5 sm:!py-4'>
    <div className='flex items-start justify-between'>
      <div className='bg-skeleton-hi w-[100px] h-[27px] rounded-lg' />
      <div className='bg-skeleton-hi w-[30px] h-[30px] rounded-[15px]' />
    </div>
    <VertSpacerMd />
    <div className='flex items-start justify-between bg-skeleton-hi rounded-[30px] p-2 sm:p-3 w-full'>
      <div className='flex items-center justify-start gap-x-[10px] sm:gap-x-[15px] max-w-full'>
        <div className='bg-skeleton-hi ml-[2px] w-[50px] h-[50px] min-w-[50px] min-h-[50px] sm:min-w-[60px] sm:min-h-[60px] sm:w-[60px] sm:h-[60px] rounded-full overflow-hidden' />
        <div className='w-full flex flex-col gap-[5px]'>
          <div className='bg-skeleton-hi rounded-lg w-[160px] h-[26px]' />
          <div className='bg-skeleton-hi rounded-lg w-[205px] h-[23px]' />
          <div className='bg-skeleton-hi rounded-lg w-[140px] h-[21px]' />
        </div>
      </div>
    </div>
  </CardContainer>
)

export const AccountDetailsSummaryButton = () => {
  const user = useCombinedUserDetails()
  const { data, isLoading, isError } = useGetContact({})

  const loading = isLoading || isError

  return (
    <AnimatePresence mode='popLayout'>
      {loading && (
        <motion.div key='settings-account-details-summary-button-skeleton' {...motionFadeIn(0.2)}>
          <AccountDetailsSummaryButtonSkeleton />
        </motion.div>
      )}
      {!loading && (
        <motion.div key='settings-account-details-summary-button' {...motionFadeIn(0.2)}>
          <ModalButton
            component={AccountDetailsUpdateModal}
            disabled={false}
            componentProps={{}}
            classes='w-full'
            enterAnimation={modalSlideUpEnterAnimation}
            leaveAnimation={modalSlideUpLeaveAnimation}
          >
            <CardContainer bgClasses='!bg-surface border border-border !px-3 !pt-2 pb-4 sm:!px-5 sm:!py-4'>
              <div className='flex items-center justify-between'>
                <SectionTitle classes='text-text-muted/70 text-left pl-[10px] pr-[5px]' text='Account' />
                <ButtonRoundSm type='button' disabled={isLoading} className='action-brand-solid' callback={() => null}>
                  <commonIcons.arrowAngled />
                </ButtonRoundSm>
              </div>
              <VertSpacerMd />
              <div className='flex items-start justify-between bg-surface-2 text-text rounded-[30px] p-2 sm:p-3 w-full whitespace-nowrap overflow-hidden'>
                <div className='flex items-center justify-start gap-x-[10px] sm:gap-x-[15px] max-w-full whitespace-nowrap overflow-hidden'>
                  <ProfileImage
                    classes='ml-[2px] w-[50px] h-[50px] min-w-[50px] min-h-[50px] sm:min-w-[60px] sm:min-h-[60px] sm:w-[60px] sm:h-[60px] rounded-full overflow-hidden border border-border'
                    user={user}
                  />
                  <div className='text-left jost-light w-full overflow-hidden'>
                    <div className='jost text-[1.1em] sm:text-[1.2em] overflow-hidden text-ellipsis'>{data?.name}</div>
                    <div className='text-[0.9em] sm:text-[1em] overflow-hidden text-ellipsis'>{data?.email}</div>
                    <div className='text-[0.9em] sm:text-[0.95em] overflow-hidden text-ellipsis'>{data?.phone}</div>
                  </div>
                </div>
              </div>
            </CardContainer>
          </ModalButton>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
